import { CurrencyPipe, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { IconCacheService } from '@finxone-platform/shared/services';
import { Constants, LookupType, MathOperator } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import dayjs from 'dayjs';
import type { OpUnitType } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Handlebars from 'handlebars';
import { take } from 'rxjs';
import { GetProfile } from '../../../actions/user-profile.action';
import { Country } from '../../../dtos/countries.dto';
import { BeneficiaryDestinationIdentifier } from '../../../services/account-service/account.type';
import { ConfigService } from '../../../services/config-service/config-service.service';
import { CountriesCurrenciesService } from '../../../services/countries-currencies-service/countries-currencies.service';
import { DataSourceService } from '../../../services/data-source-service/data-source.service';
import { SavingsProductsState } from '../../../state/savings-products.state';
import { ProfileState } from '../../../state/user-profile.state';
import { addComma, addDashesToSortCode, nthLetter, truncate, truncateFromStart } from '../string.utils';
import { registerComparisonHelpers } from './comparison.helper';
import {
  countWhenFieldDateAfter,
  countWhenFieldDateBefore,
  countWhenFieldEquals,
  countWhenFieldNotEquals,
  countWhenFieldNumberGreaterThan,
  countWhenFieldNumberLessThan,
} from './count-when-field-equals.helper';
import { countryName } from './country-full-name';
import { formatCurrencyForHelper, toPlainNumberString } from './currency-pipe.utils';
import { countryCodeToCircleFlag, currencyToCircleFlag } from './currency-to-flag.utils';
import { shrinkDecimalsHelper } from './shrink-decimals.utils';
import { iconNameToSvg } from './handlebar-icons.utils';
import { imageFromImageName } from './image.utils';
import { isAfter, isAfterAndBefore } from './is-after.helper';
import { registerMaskString } from './maskstring-helper';
import { formatDynamicTimestamp } from './timestamp.utils';
import { isFeatureEnabledPercentage } from './feature-toggle';

// Extend dayjs with plugins for enhanced functionality
// for handling dates and times in various ways
dayjs.extend(utc); //  to manipulate and format dates in Coordinated Universal Time (UTC)
dayjs.extend(timezone); // to work with different time zones
dayjs.extend(relativeTime); // for relative formatting
dayjs.extend(localizedFormat); // localized formats
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export function registerHandlerbarHelpers(
  datePipe: DatePipe,
  currencyPipe: CurrencyPipe,
  countriesCurrenciesService: CountriesCurrenciesService,
  iconCacheService: IconCacheService,
  store: Store,
  dataSourceService: DataSourceService,
  router: Router,
  configService: ConfigService,
) {
  let countriesData: Country[] = [];
  countriesCurrenciesService.getCountries().subscribe((data) => {
    countriesData = data;
  });

  registerComparisonHelpers();
  registerDateHelpers();
  registerMaskString();

  Handlebars.registerHelper('isAfter', isAfter);
  Handlebars.registerHelper('isAfterAndBefore', isAfterAndBefore);

  Handlebars.registerHelper('isFeatureEnabledPercentage', isFeatureEnabledPercentage);

  // {{countWhenFieldEquals savingsAccounts.accounts "accountType" "easy-access"}} -> count of easy-access accounts
  Handlebars.registerHelper('countWhenFieldEquals', countWhenFieldEquals);
  // {{countWhenFieldNotEquals savingsAccounts.accounts "accountType" "easy-access"}} -> count of non easy-access accounts
  Handlebars.registerHelper('countWhenFieldNotEquals', countWhenFieldNotEquals);
  // {{countWhenFieldNumberGreaterThan savingsAccounts.accounts "balance" 1000}} -> count of accounts with balance greater than 1000
  Handlebars.registerHelper('countWhenFieldNumberGreaterThan', countWhenFieldNumberGreaterThan);
  // {{countWhenFieldNumberLessThan savingsAccounts.accounts "balance" 1000}} -> count of accounts with balance less than 1000
  Handlebars.registerHelper('countWhenFieldNumberLessThan', countWhenFieldNumberLessThan);
  // {{countWhenFieldDateBefore transactions "date" someDate}} -> count of transactions before someDate
  Handlebars.registerHelper('countWhenFieldDateBefore', countWhenFieldDateBefore);
  // {{countWhenFieldDateAfter transactions "date" someDate}} -> count of transactions after someDate
  Handlebars.registerHelper('countWhenFieldDateAfter', countWhenFieldDateAfter);

  Handlebars.registerHelper('truncate', truncate);
  Handlebars.registerHelper('truncateFromEnd', truncate);
  Handlebars.registerHelper('truncateFromStart', truncateFromStart);
  Handlebars.registerHelper('nthLetter', nthLetter);
  Handlebars.registerHelper('currencyToFlag', currencyToCircleFlag);
  Handlebars.registerHelper('countryCodeToFlag', countryCodeToCircleFlag);

  Handlebars.registerHelper('countryFullName', function (countryCode: string) {
    if (countryCode?.toString()?.includes('skeleton_')) {
      return countryCode.toString();
    }
    return countryName(countryCode, countriesData);
  });

  Handlebars.registerHelper('imageFromImageName', imageFromImageName);
  Handlebars.registerHelper('addComma', addComma);
  Handlebars.registerHelper('roundNumber', roundNumber);
  Handlebars.registerHelper('localiseNumberFormat', localiseNumberFormat);
  // {{shrinkDecimals (currencyPipe balance currencyCode='GBP') 65}} -> renders the
  // decimal separator + digits at 65% of the parent font size, rounded to the
  // nearest whole pixel via CSS round(<frac>em, 1px). Defaults to 65% if the
  // percentage is omitted.
  Handlebars.registerHelper('shrinkDecimals', shrinkDecimalsHelper);
  Handlebars.registerHelper('today', today);
  Handlebars.registerHelper('math', (operator, ...args) => {
    // Remove options object from the end
    const values = args?.slice(0, -1);
    return mathFunction(operator, ...values);
  });

  // Simple helper to subtract two values
  Handlebars.registerHelper('subtract', function (a, b) {
    // Handle null, undefined, and empty string cases
    if (a === null || a === undefined || a === '') {
      return null;
    }

    if (b === null || b === undefined || b === '') {
      b = 0;
    }

    // Convert values to numbers if they are strings
    const valueA = typeof a === 'number' ? a : parseFloat(a.toString().replace(/,/g, ''));
    const valueB = typeof b === 'number' ? b : parseFloat(b.toString().replace(/,/g, ''));

    // Return the subtraction result
    return valueA - valueB;
  });

  Handlebars.registerHelper('currencyPipe', function (value: number | string, options: any) {
    return formatCurrencyForHelper(value, currencyPipe, options);
  });

  /*
   * Strips the decimal separator and decimals from a numeric value when every
   * decimal digit is `0`. Output mode is inferred from the presence of
   * `currencyCode`:
   *  - When `currencyCode` is omitted: plain number output (no currency formatting).
   *      1234.00 -> "1234"
   *      1234.03 -> "1234.03"
   *  - When `currencyCode` is provided: formats via `currencyPipe` first, then
   *    trims zero decimals.
   *      £1,234.00 -> £1,234
   *      £1,234.03 -> £1,234.03 (unchanged)
   *      £1,234.30 -> £1,234.30 (unchanged)
   *      1.234,00 € -> 1.234 €  (locale with `,` decimal separator)
   *
   * Accepts the same currency-formatting hash params as `currencyPipe`
   * (currencyCode, display, digitsInfo, locale, isSpaceRequired) when
   * `currencyCode` is provided.
   *
   * Usage:
   *   {{trimZerosFromNumber amount}}
   *   {{trimZerosFromNumber amount currencyCode='GBP'}}
   */
  Handlebars.registerHelper('trimZerosFromNumber', function (value: number | string, options: any) {
    const hasCurrencyCode =
      options?.hash?.currencyCode !== undefined &&
      options?.hash?.currencyCode !== null &&
      options?.hash?.currencyCode !== '';

    const formatted = hasCurrencyCode
      ? formatCurrencyForHelper(value, currencyPipe, options)
      : toPlainNumberString(value);

    if (formatted === null || formatted === undefined) {
      return formatted;
    }
    return formatted.replace(/([.,])0{1,2}(?=\D|$)/, '');
  });

  Handlebars.registerHelper('formatMask', function format(value, pattern) {
    if (value?.toString()?.includes('skeleton_')) {
      return value.toString();
    }
    let i = 0;
    const v = value?.toString();
    if (value) {
      return pattern.replace(/#/g, () => v[i++]);
    }

    return '-';
  });

  Handlebars.registerHelper('addDashesToSortCode', addDashesToSortCode);

  Handlebars.registerHelper('datePipe', function (value: string | number | Date, options: any) {
    if (value?.toString()?.includes('skeleton_')) {
      return value.toString();
    }
    const format = normaliseDateFormat(options.hash.format);
    return datePipe.transform(value, format, options.hash.timezone, options.hash.locale);
  });

  Handlebars.registerHelper('for', function (from, to, incr, block) {
    let accum = '';
    for (let i = from; i < to; i += incr) accum += block.fn(i);
    return accum;
  });

  /**
   * For Extracting time and date from a give string e.g: 02/05/2024 06:08
   * It returns date value formatted like 02 May 2024 (as per e.g.)
   * It returns time value in 12Hr format
   */
  Handlebars.registerHelper('splitDateTime', (dateTimeStr, type) => {
    const parts = dateTimeStr.split(' ');
    const dateStr = parts[0];
    const time = parts[1];

    // Create a date object from the date string
    const [day, month, year] = dateStr.split('/').map((num: string) => parseInt(num, 10));
    const date = new Date(year, month - 1, day);

    if (type === 'time') {
      // Split the time into hours and minutes
      // eslint-disable-next-line prefer-const
      let [hours, minutes] = time.split(':').map((num: string) => parseInt(num, 10));
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      return `${hours}:${minutes < 10 ? '0' + minutes : minutes} ${ampm}`;
    } else {
      // Format the date as '28 Nov 2023'
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    }
  });

  Handlebars.registerHelper('capitaliseFirstLetter', (input) => {
    if (!input) return;
    return input.charAt(0).toUpperCase() + input.slice(1);
  });

  Handlebars.registerHelper('capitalise', (input: string) => {
    if (!input) return input;
    return input.toString().toUpperCase();
  });

  // {{monthName allFormValues.businessOnboardingFlow.officerBirthMonth}} -> "January" (expects a 0-indexed month key, i.e. January = 0)
  Handlebars.registerHelper('monthName', (monthKey: number | string) => {
    return Constants.findMonthNameByKey(Number(monthKey)) ?? '';
  });

  Handlebars.registerHelper('removeUnderscoreAndCapitalise', (value: string) => {
    return value
      ?.split('_') // Split the string by underscores
      ?.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
      ?.join(' '); // Join the words with a space
  });

  // {{{ svgIcon 'bell' }}}
  Handlebars.registerHelper('svgIcon', function (iconName: string, iconWidth?: string, iconHeight?: string) {
    return iconNameToSvg(iconName, iconWidth, iconHeight, iconCacheService);
  });

  /**
   * Registers a Handlebars helper named 'relativeDateFormat' to format dates
   * based on a specified grouping ('day', 'week', 'month', 'year') and format string.
   *
   * @param {string | Date} date - The date to be formatted.
   * @param {'day' | 'week' | 'month' | 'year'} grouping - The grouping category for formatting.
   * @param {string} format - The desired date format string.
   * @returns {string} - The formatted date string or an empty string if the date is invalid.
   *
   * The function uses predefined date formats for each grouping and provides
   * special handling for week formats by calculating the start and end of the week.
   * If the specified format does not exist for the given grouping, a default format is used.
   */
  Handlebars.registerHelper(
    'relativeDateFormat',
    function (
      date: string | Date,
      grouping: 'day' | 'week' | 'month' | 'year',
      format: string,
      isRelativeDate = false,
    ) {
      const inputDate = dayjs(date);
      const now = dayjs();

      if (!inputDate.isValid()) {
        return '';
      }

      // Check if the format exists for the given grouping
      if (!DATE_FORMATS[grouping]?.[format]) {
        return inputDate.format('L'); // Default format
      }

      if (isRelativeDate) {
        // Handle relative date formats
        if (grouping === 'day') {
          if (inputDate.isSame(now, 'day')) {
            return 'Today';
          }
          if (inputDate.isSame(now.clone().subtract(1, 'day'), 'day')) {
            return 'Yesterday';
          }
        } else if (grouping === 'week') {
          if (inputDate.isSame(now, 'week')) {
            return 'This Week';
          }
          if (inputDate.isSame(now.clone().subtract(1, 'week'), 'week')) {
            return 'Last Week';
          }
        } else if (grouping === 'month') {
          if (inputDate.isSame(now, 'month')) {
            return 'This Month';
          }
          if (inputDate.isSame(now.clone().subtract(1, 'month'), 'month')) {
            return 'Last Month';
          }
        }
      }

      // Special handling for week formats
      if (grouping === 'week') {
        const weekStart = inputDate.clone().startOf('week');
        const weekEnd = inputDate.clone().endOf('week');

        const formatMap: Record<string, string> = {
          'Week of DD MMM YYYY': `Week of ${weekStart.format('DD MMM YYYY')}`,
          'DD MMM - DD MMM YYYY': `${weekStart.format('DD MMM')} - ${weekEnd.format('DD MMM YYYY')}`,
          'MMM DD - DD, YYYY': `${weekStart.format('MMM DD')} - ${weekEnd.format('DD, YYYY')}`,
          'Week of DD MMMM YYYY': `Week of ${weekStart.format('DD MMMM YYYY')}`,
          'DD MMMM - DD MMMM YYYY': `${weekStart.format('DD MMMM')} - ${weekEnd.format('DD MMMM YYYY')}`,
          'MMMM DD - DD, YYYY': `${weekStart.format('MMMM DD')} - ${weekEnd.format('DD, YYYY')}`,
        };

        return formatMap[format] || formatMap['Week of DD MMM YYYY'];
      }

      // For day, month, and year groupings, use the format directly
      return inputDate.format(DATE_FORMATS[grouping][format]);
    },
  );

  /**
   * Can be used in a handler bar to fetch a product detail based on product code or the form state for example:
    {{#with (getSavingProduct onboardingForm.productCode) }}
       {{ productName }}  {{ accountType }} {{ productCode }} {{ interestRate }} {{ termMonths }} {{ expiry }} {{ aer }}
    {{/with}}
    * @param category: Param for returning available products specific to renewal, if not present it will look for availableProducts in savings account state
   */
  Handlebars.registerHelper('getSavingProduct', (productCode: string | number, category?: string) => {
    const products = !category
      ? store.selectSnapshot(SavingsProductsState.getAvailableProducts)
      : store.selectSnapshot(SavingsProductsState.getAvailableProductsForRenewal);
    return products?.find((product) => product.productCode === productCode?.toString());
  });

  Handlebars.registerHelper('getDataLookupLabel', (type: LookupType, value: string) => {
    const lookupData = dataSourceService?.dataSources?.[type];
    if (lookupData) {
      const found = lookupData.find((item) => item.value === value);
      return found?.label || '';
    }

    dataSourceService.getDataSourceByType(type).pipe(take(1)).subscribe();
    return '';
  });

  Handlebars.registerHelper('rePollProfile', (match: string[], paramToCheck: string, timerMs = 3000) => {
    if (configService.getActiveConfigType().isWorkflow) {
      return;
    }

    const INTERVAL_KEY = '__rePollProfileIntervalId';

    // dont create another interval if one exists
    if ((globalThis as any)[INTERVAL_KEY]) return;

    let matchValues: string[];
    if (Array.isArray(match)) {
      matchValues = match;
    } else {
      return;
    }
    const profile = store.selectSnapshot(ProfileState.getProfile) as any;
    // If paramToCheck is a key on the profile, use that value; otherwise treat paramToCheck as the value itself.
    const currentParamValue =
      profile && Object.hasOwn(profile, paramToCheck) ? profile[paramToCheck] : paramToCheck;
    //if the value already matches no need to repoll GetProfile, just exit
    if (matchValues.includes(currentParamValue)) {
      return;
    }

    const intervalId = setInterval(() => {
      const profile = store.selectSnapshot(ProfileState.getProfile) as any;
      const currentParamValue =
        profile && Object.hasOwn(profile, paramToCheck) ? profile[paramToCheck] : paramToCheck;
      store.dispatch(new GetProfile());
      if (matchValues.includes(currentParamValue)) {
        clearInterval(intervalId);
        // remove the global reference so next render creates a new interval if needed
        try {
          delete (globalThis as any)[INTERVAL_KEY];
          return;
        } catch {
          return;
        }
      }
    }, timerMs);
    // Store interval id globally so future renders can detect theres already one and stop it from making duplicates
    try {
      (globalThis as any)[INTERVAL_KEY] = intervalId;
    } catch {
      return;
    }
  });

  Handlebars.registerHelper('redirectIfMatch', (value1: string, value2: string, url: string) => {
    if (configService.getActiveConfigType().isWorkflow) {
      return;
    }
    if (value1 === value2) {
      router.navigateByUrl(url);
    }
  });

  Handlebars.registerHelper('array', function (...args: any[]): any[] {
    // Remove Handlebars options object (always the last argument)
    return args.slice(0, -1);
  });
}

const DATE_FORMATS: Record<string, Record<string, string>> = {
  day: {
    'DD MMM YYYY': 'DD MMM YYYY',
    'DD MMM': 'DD MMM',
    'DD MMMM YYYY': 'DD MMMM YYYY',
    'DD MMMM': 'DD MMMM',
    'MMM DD, YYYY': 'MMM DD, YYYY',
    'MMMM DD, YYYY': 'MMMM DD, YYYY',
    'DD-MM-YYYY': 'DD-MM-YYYY',
    'MM/DD/YYYY': 'MM/DD/YYYY',
  },
  week: {
    'Week of DD MMM YYYY': 'Week of DD MMM YYYY',
    'DD MMM - DD MMM YYYY': 'DD MMM - DD MMM YYYY',
    'MMM DD - DD, YYYY': 'MMM DD - DD, YYYY',
    'Week of DD MMMM YYYY': 'Week of DD MMMM YYYY',
    'DD MMMM - DD MMMM YYYY': 'DD MMMM - DD MMMM YYYY',
    'MMMM DD - DD, YYYY': 'MMMM DD - DD, YYYY',
  },
  month: {
    'MMMM YYYY': 'MMMM YYYY',
    'MMM YYYY': 'MMM YYYY',
  },
  year: {
    YYYY: 'YYYY',
  },
};

// Looks for the routing detail in the beneficiary destination identifier object
// and returns the first non-empty value found. If no value is found, it returns null.
// If the key is 'sortCode', it formats the value using addDashesToSortCode function.
Handlebars.registerHelper(
  'getRoutingDetail',
  function (destinationIdentifier: BeneficiaryDestinationIdentifier) {
    if (!destinationIdentifier) return '--';
    const routingKeys: (keyof BeneficiaryDestinationIdentifier)[] = [
      'sortCode',
      'aba',
      'rtnCanada',
      'clabe',
      'bsbCode',
      'ifsc',
      'cnaps',
    ];
    for (const key of routingKeys) {
      if (destinationIdentifier[key]) {
        if (key === 'sortCode') {
          return addDashesToSortCode(destinationIdentifier[key] ?? '');
        }
        return destinationIdentifier[key];
      }
    }
    return null;
  },
);

//Helper for dynamic timestamping like 'just now', '1 min ago', '2 hours ago' etc
Handlebars.registerHelper('dynamicTimestamp', function (date: string | Date, dateFormat?: string) {
  return formatDynamicTimestamp(date, dateFormat);
});

/**
 * Rounds a numeric value using the specified method.
 *
 * Supports the following rounding types:
 * - `'round'` (default): Rounds to the nearest value using `Math.round()`
 * - `'floor'`: Rounds down using `Math.floor()`
 * - `'ceil'`: Rounds up using `Math.ceil()`
 * - `'fixed'`: Formats the number as a string with a fixed number of decimal places using `.toFixed()`
 *
 * Handles both string and number inputs. If the input is not a valid number, it is returned as-is.
 *
 * @returns A number (or string for 'fixed') with the applied rounding, or the original value if invalid.
 *
 * @example
 * {{ roundNumber '12.567' 'round' 1  }} // 12.6
 * {{ roundNumber '15.89' 'floor' }} // 15
 * {{ roundNumber '7.2' 'ceil' }} // 8
 * {{ roundNumber '9.999' 'fixed' 2 }} // "10.00"
 * {{ roundNumber 'abc' 'round' }} // "abc" (invalid input, returned as-is)
 */
const roundNumber = (
  value: number | string,
  type: 'round' | 'floor' | 'ceil' | 'fixed' = 'round',
  digits = 0,
): number | string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return value;

  switch (type) {
    case 'floor':
      return Math.floor(num);
    case 'ceil':
      return Math.ceil(num);
    case 'fixed':
      return num.toFixed(digits); // returns string
    case 'round':
    default: {
      const multiplier = Math.pow(10, digits);
      return Math.round(num * multiplier) / multiplier;
    }
  }
};

/**
 * Formats a numeric value according to a specified locale using locale-sensitive formatting.
 *
 * This function converts a number (or numeric string) into a human-readable string
 * formatted with appropriate digit group separators and decimal symbols based on the locale.
 *
 * If the input value is not a valid number, it returns the original value unchanged.
 *
 * @example
 * {{ localiseNumberFormat '1234567.89' }} // Uses browser locale
 * {{ localiseNumberFormat '1234567.89' 'en-US' }} // "1,234,567.89"
 * {{ localiseNumberFormat '1234567.89' 'fr-FR' }} // "1 234 567,89"
 * {{ localiseNumberFormat 'abc' }} // "abc" because input is not a valid number
 */
export const localiseNumberFormat = (value: string | number, locale?: string) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return value;

  return new Intl.NumberFormat(locale).format(num);
};

/**
 * Handlebars helper: {{ today "MMMM DD, YYYY [at] hh:mm A" }}
 * Displays the current device time using a Moment.js format string.
 *
 * @param formatString - A Moment.js format string (e.g., "MMMM DD, YYYY [at] hh:mm A")
 * @returns Formatted current datetime string.
 * @example
 * {{ today "MMMM DD, YYYY [at] hh:mm A" }}  // June 19, 2025 at 07:25 PM
 */
const today = (formatString: string) => {
  return dayjs().format(formatString);
};

/**
 * Normalises a date format string for use with Angular's DatePipe by converting
 * uppercase 'YYYY' (ISO week-year, used by Moment.js/dayjs) to lowercase 'yyyy'
 * (calendar year, required by Angular DatePipe / Unicode LDML standard).
 *
 * @example
 * normaliseDateFormat('YYYY-MM-DD')   // 'yyyy-MM-dd' — no, only YYYY -> yyyy
 * normaliseDateFormat('DD/MM/YYYY')   // 'DD/MM/yyyy'
 * normaliseDateFormat('MMM YYYY')     // 'MMM yyyy'
 * normaliseDateFormat(undefined)      // undefined
 */
export const normaliseDateFormat = (format: string | undefined): string | undefined => {
  if (!format) return format;
  // Replace standalone YYYY (not preceded or followed by another Y) with yyyy
  return format.replace(/YYYY/g, 'yyyy');
};

/**
 * Performs mathematical operations (+, -, *, /, %, quotient, remainder) on numeric values.
 * Supports multiple operands for '+' and '*' operations.
 *
 * @param operator - The mathematical operator to apply ('+', '-', '*', '/', '%', 'quotient', 'remainder').
 * @param values - A list of values (strings or numbers) to apply the operation on.
 * @returns The calculated result as a number, or an empty string if invalid input is provided.
 *
 * @example
 * {{ math '*' '100' '3' '3' }} // 900
 * {{ math '+' '100' '3' '3' }} // 106
 * {{ math '/' '100' '3' }} // 33.333333333333336
 * {{ math '%' '100' '3' }} // 1
 * {{ math '-' '100' '3' '23' }} // 74
 * {{ math 'quotient' '100' '3' }}  // 33
 * {{ math 'remainder' '100' '3' }} // 1
 */

export const mathFunction = (operator: MathOperator, ...values: (string | number)[]): number | string => {
  const parsedValues = values.map((v) => (typeof v === 'string' ? parseFloat(v) : v));

  if (parsedValues.some((val) => isNaN(val))) return '';

  switch (operator) {
    case '+':
    case 'add':
      return parsedValues.reduce((acc, val) => acc + val, 0);
    case '*':
    case 'multiply':
      return parsedValues.reduce((acc, val) => acc * val, 1);
    case '-':
    case 'subtract':
      return parsedValues.reduce((acc, val) => acc - val);
    case '/':
    case 'divide':
      return parsedValues.reduce((acc, val) => (val === 0 ? NaN : acc / val));
    case '%':
      return parsedValues.reduce((acc, val) => acc % val);
    case 'quotient':
      return parsedValues.reduce((acc, val) => Math.floor(acc / val));
    case 'remainder':
      return parsedValues.reduce((acc, val) => acc % val);
    default:
      return '';
  }
};

/**
 * Register Handlebars date helpers using dayjs
 */
export function registerDateHelpers() {
  // Returns the current date in a given format (default: YYYY-MM-DD)
  Handlebars.registerHelper('todaysDate', (format = 'YYYY-MM-DD') => dayjs().format(format));

  // Check if a date is same or before another date
  // {{dateIsSameOrBefore "20-09-2025" "20-09-2025" "day" "DD-MM-YYYY"}} -> true
  // {{dateIsSameOrBefore "19-09-2025" "20-09-2025" "day" "DD-MM-YYYY"}} -> true
  // {{dateIsSameOrBefore "21-09-2025" "20-09-2025" "day" "DD-MM-YYYY"}} ->false
  Handlebars.registerHelper(
    'dateIsSameOrBefore',
    (d1: string | Date, d2: string | Date, unit?: OpUnitType, inputFormat = 'YYYY-MM-DD') => {
      return dayjs(d1, inputFormat).isSameOrBefore(dayjs(d2, inputFormat), unit);
    },
  );

  // Check if a date is same or after another date
  // {{dateIsSameOrAfter "24-09-2025" "24-09-2025" "day" "DD-MM-YYYY"}} -> true
  // {{dateIsSameOrAfter "21-09-2025" "20-09-2025" "day" "DD-MM-YYYY"}} -> true
  // {{dateIsSameOrAfter "19-09-2025" "20-09-2025" "day" "DD-MM-YYYY"}} -> false
  Handlebars.registerHelper(
    'dateIsSameOrAfter',
    (d1: string | Date, d2: string | Date, unit?: OpUnitType, inputFormat = 'YYYY-MM-DD') => {
      return dayjs(d1, inputFormat).isSameOrAfter(dayjs(d2, inputFormat), unit);
    },
  );

  // Returns a human-readable difference between the given date and today.
  // Assumes "today" is 24-09-2025 in the examples.
  // - If the date is in the past: "{{dateFromNow "22-09-2025" "DD-MM-YYYY"}}" -> "2 days ago"
  // - If the date is in the future: "{{dateToNow "28-09-2025" "DD-MM-YYYY"}}" -> "in 4 days"
  Handlebars.registerHelper('dateFromNow', (date: string | Date, inputFormat = 'YYYY-MM-DD') => {
    const d = dayjs(date, inputFormat).startOf('day'); // ignore time
    const today = dayjs().startOf('day'); // ignore current time
    return d.isValid() ? d.from(today) : '';
  });

  // This helper checks whether today's date falls within a range that starts a specified number of days before a given end date, up to and including the end date itself.
  // Example: {{showBeforeEndRange "30-09-2025" 10 "DD-MM-YYYY"}}
  // -> true if today is between 20-09-2025 and 30-09-2025
  Handlebars.registerHelper(
    'showBeforeEndRange',
    (endDate: string | Date, daysBefore: number, inputFormat = 'YYYY-MM-DD') => {
      const end = dayjs(endDate, inputFormat).startOf('day');
      const start = end.subtract(daysBefore, 'day').startOf('day');
      const today = dayjs().startOf('day');
      return today.isSameOrAfter(start, 'day') && today.isSameOrBefore(end, 'day');
    },
  );

  // Adds a specific number of days, months, years, or other units to a given date,and returns the new date in the desired format.
  // unit: day | month | week | year
  // eg: {{addToDate "05-10-2025" 12 "day" "DD-MM-YYYY"}}
  // Nested eg: {{addToDate (addToDate "05-10-2025" 5 "day" "DD-MM-YYYY") 1 "month" "DD-MM-YYYY"}}
  Handlebars.registerHelper(
    'addToDate',
    (date: string | Date, amount: number, unit = 'day', format = 'YYYY-MM-DD') => {
      // Ensure format is a string (handle Handlebars options object)
      const outputFormat = typeof format === 'string' ? format : 'YYYY-MM-DD';

      // Try parsing with the output format strictly first (fixes ambiguous dates like 05-10-2025)
      let d = dayjs(date, outputFormat, true);

      // If strict parsing fails (e.g., input is ISO or Date object but output format is custom), fallback to auto-parsing
      if (!d.isValid()) {
        d = dayjs(date);
      }

      if (!d.isValid() || isNaN(amount)) return '';
      return d.add(Number(amount), unit).format(outputFormat);
    },
  );
}
